import React, { useState } from 'react';
import { Case, CaseResolutionData } from '../types';
import { LoadingSpinner } from './LoadingSpinner';

interface CaseResolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onResolve: (data: CaseResolutionData) => Promise<void>;
  case: Case;
  isSubmitting?: boolean;
}

export function CaseResolutionModal({ 
  isOpen, 
  onClose, 
  onResolve, 
  case: caseData, 
  isSubmitting = false 
}: CaseResolutionModalProps) {
  const [finalDiagnosis, setFinalDiagnosis] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset errors
    setErrors({});

    // Validate final diagnosis
    if (!finalDiagnosis.trim()) {
      setErrors({ finalDiagnosis: 'Η τελική διάγνωση είναι υποχρεωτική' });
      return;
    }

    try {
      await onResolve({
        finalDiagnosis: finalDiagnosis.trim(),
        resolutionNotes: resolutionNotes.trim() || undefined
      });
      
      // Reset form
      setFinalDiagnosis('');
      setResolutionNotes('');
      onClose();
    } catch (error) {
      console.error('Resolution failed:', error);
      setErrors({ submit: 'Αποτυχία επίλυσης περιστατικού' });
    }
  };

  const handleClose = () => {
    setFinalDiagnosis('');
    setResolutionNotes('');
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Επίλυση Περιστατικού
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              {caseData.title}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Case Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Περίληψη Περιστατικού</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p><strong>Τίτλος:</strong> {caseData.title}</p>
              <p><strong>Τύπος:</strong> {caseData.caseType}</p>
              {caseData.tissueType && (
                <p><strong>Ιστός:</strong> {caseData.tissueType}</p>
              )}
              {caseData.urgencyLevel && (
                <p><strong>Επείγον:</strong> {
                  caseData.urgencyLevel === 'low' ? 'Εκπαιδευτικό' :
                  caseData.urgencyLevel === 'normal' ? 'Κανονική' :
                  caseData.urgencyLevel === 'high' ? 'Υψηλή' : 'Επείγον'
                }</p>
              )}
            </div>
          </div>

          {/* Final Diagnosis */}
          <div className="mb-6">
            <label htmlFor="finalDiagnosis" className="block text-sm font-medium text-gray-700 mb-2">
              Τελική Διάγνωση *
            </label>
            <textarea
              id="finalDiagnosis"
              value={finalDiagnosis}
              onChange={(e) => setFinalDiagnosis(e.target.value)}
              className={`w-full px-3 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.finalDiagnosis ? 'border-red-300' : 'border-gray-300'
              }`}
              rows={4}
              placeholder="Εισάγετε την τελική διάγνωση για αυτό το περιστατικό..."
              disabled={isSubmitting}
              required
            />
            {errors.finalDiagnosis && (
              <p className="mt-1 text-sm text-red-600">{errors.finalDiagnosis}</p>
            )}
          </div>

          {/* Resolution Notes */}
          <div className="mb-6">
            <label htmlFor="resolutionNotes" className="block text-sm font-medium text-gray-700 mb-2">
              Σημειώσεις Επίλυσης (Προαιρετικό)
            </label>
            <textarea
              id="resolutionNotes"
              value={resolutionNotes}
              onChange={(e) => setResolutionNotes(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows={3}
              placeholder="Προσθέστε επιπλέον σημειώσεις ή σχόλια για την επίλυση..."
              disabled={isSubmitting}
            />
          </div>

          {/* Warning */}
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">Προσοχή</h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Με την επίλυση του περιστατικού, το status θα αλλάξει σε "Λυμένο" και δεν θα δέχεται νέα σχόλια. 
                    Μπορείτε να επαναφέρετε το περιστατικό αργότερα αν χρειαστεί.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Error */}
          {errors.submit && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{errors.submit}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              disabled={isSubmitting}
            >
              Ακύρωση
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isSubmitting || !finalDiagnosis.trim()}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner size="sm" />
                  <span className="ml-2">Επίλυση...</span>
                </>
              ) : (
                'Επίλυση Περιστατικού'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}